/**
 * useUrlState - URL State Management Hook for Forecast V3
 *
 * Syncs forecast state to URL query parameters for:
 * - Shareable deep links
 * - Browser back/forward navigation
 * - Persistent state on page refresh
 *
 * URL format: /forecast-v3?tab=workspace&version=26&year=2026&month=2&account=1234&panel=driver
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ActiveTab, DetailPanelTab } from '../types';

// URL parameter names
const URL_PARAMS = {
  TAB: 'tab',
  LOCATION: 'location',
  VERSION: 'version',
  YEAR: 'year',
  MONTH: 'month',
  ACCOUNT: 'account',
  PANEL: 'panel',
} as const;

// Valid values for validation
const VALID_TABS: ActiveTab[] = ['workspace', 'versions', 'compare', 'assumptions', 'health', 'risk', 'analytics', 'notes', 'labor'];
const VALID_PANELS: DetailPanelTab[] = ['driver', 'adjustments', 'overrides', 'notes'];

export interface UrlState {
  activeTab: ActiveTab;
  locationId: number | null;
  versionId: number | null;
  year: number;
  activeMonth: number | null;
  selectedAccountId: number | null;
  detailPanelTab: DetailPanelTab;
}

export interface UseUrlStateReturn {
  /** Current state parsed from URL */
  urlState: UrlState;
  /** Update URL parameters (defaults to replace mode to avoid history spam) */
  updateUrl: (updates: Partial<UrlState>, options?: { replace?: boolean }) => void;
  /** Check if URL has any forecast-specific params (for initialization detection) */
  hasUrlParams: boolean;
  /** Clear all forecast URL params */
  clearUrlParams: () => void;
}

/**
 * Parse and validate URL state with sensible defaults
 */
function parseUrlState(searchParams: URLSearchParams): UrlState {
  // Tab - validate against allowed values
  const tabParam = searchParams.get(URL_PARAMS.TAB);
  const activeTab: ActiveTab = VALID_TABS.includes(tabParam as ActiveTab)
    ? (tabParam as ActiveTab)
    : 'workspace';

  // Location ID - parse as number or null
  const locationParam = searchParams.get(URL_PARAMS.LOCATION);
  const locationId = locationParam ? parseInt(locationParam, 10) : null;

  // Version ID - parse as number or null
  const versionParam = searchParams.get(URL_PARAMS.VERSION);
  const versionId = versionParam ? parseInt(versionParam, 10) : null;

  // Year - parse as number with current year default
  const yearParam = searchParams.get(URL_PARAMS.YEAR);
  const year = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();

  // Month - parse as number (1-12) or null
  const monthParam = searchParams.get(URL_PARAMS.MONTH);
  const activeMonth = monthParam ? parseInt(monthParam, 10) : null;

  // Account ID - parse as number or null
  const accountParam = searchParams.get(URL_PARAMS.ACCOUNT);
  const selectedAccountId = accountParam ? parseInt(accountParam, 10) : null;

  // Panel tab - validate against allowed values
  const panelParam = searchParams.get(URL_PARAMS.PANEL);
  const detailPanelTab: DetailPanelTab = VALID_PANELS.includes(panelParam as DetailPanelTab)
    ? (panelParam as DetailPanelTab)
    : 'driver';

  return {
    activeTab,
    locationId: isNaN(locationId!) ? null : locationId,
    versionId: isNaN(versionId!) ? null : versionId,
    year: isNaN(year) ? new Date().getFullYear() : year,
    activeMonth: activeMonth && !isNaN(activeMonth) && activeMonth >= 1 && activeMonth <= 12
      ? activeMonth
      : null,
    selectedAccountId: isNaN(selectedAccountId!) ? null : selectedAccountId,
    detailPanelTab,
  };
}

/**
 * Hook for managing forecast URL state
 */
export function useUrlState(): UseUrlStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current URL state
  const urlState = useMemo(() => parseUrlState(searchParams), [searchParams]);

  // Check if URL has any forecast-specific params
  const hasUrlParams = useMemo(() => {
    return Object.values(URL_PARAMS).some(param => searchParams.has(param));
  }, [searchParams]);

  // Update URL with new state values
  const updateUrl = useCallback((
    updates: Partial<UrlState>,
    options: { replace?: boolean } = { replace: true }
  ) => {
    const newParams = new URLSearchParams(searchParams);

    // Tab
    if (updates.activeTab !== undefined) {
      if (updates.activeTab && updates.activeTab !== 'workspace') {
        newParams.set(URL_PARAMS.TAB, updates.activeTab);
      } else {
        newParams.delete(URL_PARAMS.TAB); // 'workspace' is default, don't clutter URL
      }
    }

    // Location
    if (updates.locationId !== undefined) {
      if (updates.locationId !== null) {
        newParams.set(URL_PARAMS.LOCATION, String(updates.locationId));
      } else {
        newParams.delete(URL_PARAMS.LOCATION);
      }
    }

    // Version
    if (updates.versionId !== undefined) {
      if (updates.versionId !== null) {
        newParams.set(URL_PARAMS.VERSION, String(updates.versionId));
      } else {
        newParams.delete(URL_PARAMS.VERSION);
      }
    }

    // Year
    if (updates.year !== undefined) {
      const currentYear = new Date().getFullYear();
      if (updates.year !== currentYear) {
        newParams.set(URL_PARAMS.YEAR, String(updates.year));
      } else {
        newParams.delete(URL_PARAMS.YEAR); // Current year is default, don't clutter URL
      }
    }

    // Month
    if (updates.activeMonth !== undefined) {
      if (updates.activeMonth !== null) {
        newParams.set(URL_PARAMS.MONTH, String(updates.activeMonth));
      } else {
        newParams.delete(URL_PARAMS.MONTH);
      }
    }

    // Account
    if (updates.selectedAccountId !== undefined) {
      if (updates.selectedAccountId !== null) {
        newParams.set(URL_PARAMS.ACCOUNT, String(updates.selectedAccountId));
      } else {
        newParams.delete(URL_PARAMS.ACCOUNT);
      }
    }

    // Panel tab
    if (updates.detailPanelTab !== undefined) {
      if (updates.detailPanelTab && updates.detailPanelTab !== 'driver') {
        newParams.set(URL_PARAMS.PANEL, updates.detailPanelTab);
      } else {
        newParams.delete(URL_PARAMS.PANEL); // 'driver' is default, don't clutter URL
      }
    }

    setSearchParams(newParams, { replace: options.replace });
  }, [searchParams, setSearchParams]);

  // Clear all forecast URL params
  const clearUrlParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    Object.values(URL_PARAMS).forEach(param => newParams.delete(param));
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    urlState,
    updateUrl,
    hasUrlParams,
    clearUrlParams,
  };
}

export default useUrlState;
